/**
 * Include Children Toggle
 *
 * Dropdown for toggling between Current Level Only and Aggregated (including subsidiaries) views.
 * Shared across Dashboard, Scope, and Insights pages.
 */

import { Building, Network, ChevronDown } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@g17eco/ui-modern/components/ui/dropdown-menu';
import { cn } from '@g17eco/ui-modern/lib/utils';

interface IncludeChildrenToggleProps {
  disabled?: boolean;
  includeChildren: boolean;
  onChange: (includeChildren: boolean) => void;
}

export const IncludeChildrenToggle = ({ disabled, includeChildren, onChange }: IncludeChildrenToggleProps) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant='outline' disabled={disabled}>
        {includeChildren ? <Network className='tw:size-4' /> : <Building className='tw:size-4' />}
        {includeChildren ? 'Aggregated' : 'Current Level Only'}
        <Badge variant='secondary' className='tw:ml-1 tw:text-[10px] tw:px-1.5 tw:py-0'>
          {includeChildren ? 'All' : '1'}
        </Badge>
        <ChevronDown className='tw:size-3.5' />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align='end'>
      <DropdownMenuItem onClick={() => onChange(false)}>
        <Building className={cn('tw:size-4', !includeChildren && 'tw:text-emerald-600')} />
        <div>
          <div className={cn('tw:font-medium', !includeChildren && 'tw:text-emerald-600')}>Current Level Only</div>
          <div className='tw:text-xs tw:text-muted-foreground'>Parent company emissions only</div>
        </div>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onChange(true)}>
        <Network className={cn('tw:size-4', includeChildren && 'tw:text-emerald-600')} />
        <div>
          <div className={cn('tw:font-medium', includeChildren && 'tw:text-emerald-600')}>Aggregated</div>
          <div className='tw:text-xs tw:text-muted-foreground'>Include subsidiaries &amp; facilities</div>
        </div>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);
